import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Banner, Button, DataTable, Details, Dialog, Divider, Select, Skeleton, StatusChip,
  TextField, useSnackbar,
} from '@meshble/molo';
import { api, must, mustOk, type Backup, type BackupCoverage } from '../lib/api';
import { useServers } from '../lib/dati';
import { ETICHETTA as ETICHETTA_ISTANZA, TONO_ISTANZA } from './instances';
import { quandoRelativo } from '../lib/attivita';
import { Download, Play, Restore, Shield } from '../icons';
import { Anello, Logo } from '../brand';
import { localeIntl, t } from '../lib/lingua';
import { ConPermesso } from '../permesso';
import { usePermessiSu } from '../session';

/**
 * La copertura dei backup dell'organizzazione, e da lì tutto ciò che si fa a
 * un'istanza: pianificarne i backup, farne uno adesso, ripristinarne uno.
 *
 * La tavola elenca **tutte** le istanze, comprese quelle senza pianificazione e
 * senza un backup mai fatto. Sono quelle che contano: è per vederle senza
 * aprire venti pagine che questa vista esiste, ed è la pagina che si gira a un
 * auditor quando chiede la A.8.13.
 */

/** Gli stati di `backups.status` in `00009_backup_esecuzione.sql`. */
export const ESITO: Record<string, { status: string; label: string }> = {
  running: { status: 'creating', label: 'in corso' },
  ok: { status: 'ready', label: 'riuscito' },
  // `verified` non è un successo in più: è l'unico stato che dice che qualcuno
  // ha riaperto davvero quello snapshot.
  verified: { status: 'ready', label: 'verificato' },
  failed: { status: 'error', label: 'fallito' },
  verify_failed: { status: 'error', label: 'non leggibile' },
};

/** Le pianificazioni che si scelgono da un menu; tutto il resto è «personalizzata». */
export const PIANI: Array<{ value: string; label: string }> = [
  { value: '@hourly', label: 'Ogni ora' },
  { value: '@daily', label: 'Ogni giorno, di notte' },
  { value: '@weekly', label: 'Ogni settimana' },
];

/** Come si dice una pianificazione: «Ogni giorno», o il cron com'è se non lo conosciamo. */
export function nomePiano(cron: string | undefined): string {
  if (!cron) return t('Non pianificati');
  const noto = PIANI.find((p) => p.value === cron);
  return noto ? t(noto.label) : t('Personalizzata ({cron})', { cron });
}

function quando(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(localeIntl(), { dateStyle: 'short', timeStyle: 'short' });
}

/** Byte in una misura che si legge a colpo d'occhio, senza decimali inutili. */
export function peso(byte?: number): string {
  if (!byte) return '—';
  const unita = ['B', 'kB', 'MB', 'GB', 'TB'];
  let v = byte;
  let i = 0;
  while (v >= 1024 && i < unita.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)} ${unita[i]}`;
}

export function Copertura({ slug, apriIstanza }: { slug: string; apriIstanza?: string }) {
  const navigate = useNavigate();
  // Si tiene l'id, non la riga: la riga si rilegge dalla tavola a ogni
  // aggiornamento, così il pannello aperto vede «Attivi» appena la
  // pianificazione è salvata, invece di restare su «Non attivi» finché non lo
  // si chiude e riapre (rilievo 6 del DR del 18/09/2026).
  const [apertaId, setApertaId] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['backup-coverage', slug],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/backup-coverage', { params: { path: { slug } } })),
    // Finché un backup è in corso la riga si aggiorna da sola: è un lavoro di
    // minuti, e chi l'ha appena lanciato sta guardando questa tavola.
    refetchInterval: (r) =>
      (r.state.data?.coverage ?? []).some((c) => c.lastStatus === 'running') ? 5000 : false,
  });
  const righe = q.data?.coverage ?? [];
  const aperta = apertaId ? righe.find((c) => c.instanceId === apertaId) ?? null : null;

  // Arrivare con `?istanza=<id>` apre subito il suo pannello: è il bottone
  // «Backup» nell'elenco delle istanze.
  useEffect(() => {
    if (!apriIstanza || !q.data) return;
    if (righe.some((c) => c.instanceId === apriIstanza)) setApertaId(apriIstanza);
  }, [apriIstanza, q.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const chiudi = () => {
    setApertaId(null);
    if (apriIstanza) navigate({ to: '/orgs/$slug/backup', params: { slug }, search: {}, replace: true });
  };

  const scoperte = righe.filter((c) => !c.cron && !c.removed).length;

  return (
    <section className="md-stack">
      <div className="md-section-head">
        <h2 className="md-title-medium">{t('Le tue istanze')}</h2>
        {scoperte > 0 && (
          <span className="md-body-small" style={{ color: 'var(--md-sys-color-error)' }}>
            {scoperte === 1 ? t('Una istanza senza backup automatici') : t('{n} istanze senza backup automatici', { n: scoperte })}
          </span>
        )}
      </div>

      {q.isPending && <Skeleton height="8rem" />}
      {q.error && (
        <Banner tone="error" title={t('Non riesco a leggere la copertura')}>{q.error.message}</Banner>
      )}

      {q.isSuccess && righe.length === 0 && (
        <Banner tone="info" title={t('Nessuna istanza')}>
          {t("Quando creerai un'istanza, qui vedrai se ha i backup e di quando è l'ultimo.")}
        </Banner>
      )}

      {righe.length > 0 && (
        <DataTable
          columns={[
            { key: 'istanza', header: t('Istanza') },
            { key: 'pianificazione', header: t('Backup automatici') },
            { key: 'ultimo', header: t('Ultimo backup') },
            { key: 'prova', header: t('Prova di ripristino') },
            { key: 'azioni', header: '' },
          ]}
          rowKey={(r) => String(r.id)}
          onRowClick={(r) => setApertaId(String(r.id))}
          rows={righe.map((c) => ({
            id: c.instanceId,
            istanza: (
              <span className="md-stack__inline" style={{ alignItems: 'center' }}>
                <span className="md-body-large">{c.instanceName}</span>
                {/* Un'istanza rimossa — o andata via col suo server — resta
                    qui finché ha un backup: è da questa riga che rinasce. */}
                {c.removed ? (
                  <StatusChip status="stopped" label={t('Rimossa · era su {server}', { server: c.serverName })} tone="neutral" />
                ) : c.instanceStatus !== 'running' && (
                  // Un'istanza che non è attiva non ha niente da salvare, e
                  // «senza backup» non deve sembrare una dimenticanza.
                  <StatusChip status={c.instanceStatus} label={t(ETICHETTA_ISTANZA[c.instanceStatus] ?? c.instanceStatus)} tone={TONO_ISTANZA[c.instanceStatus] ?? 'neutral'} />
                )}
              </span>
            ),
            pianificazione: c.removed ? (
              <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>—</span>
            ) : c.cron ? (
              <StatusChip status="ready" label={nomePiano(c.cron)} />
            ) : (
              // Il caso che questa pagina esiste per mostrare: un'istanza di cui
              // nessuno fa una copia, detto senza girarci intorno.
              <StatusChip status="error" label={t('Non pianificati')} />
            ),
            ultimo: <Ultimo riga={c} />,
            prova: <Prova riga={c} />,
            azioni: (
              <div className="md-stack__inline" style={{ justifyContent: 'flex-end' }}>
                <Button variant="text" onClick={() => setApertaId(c.instanceId)}>{c.removed ? t('Ripristina') : t('Gestisci')}</Button>
              </div>
            ),
          }))}
        />
      )}

      {aperta && (
        <PannelloIstanza slug={slug} riga={aperta} onClose={chiudi} />
      )}
    </section>
  );
}

function Ultimo({ riga }: { riga: BackupCoverage }) {
  if (!riga.lastStatus) {
    return <StatusChip status="error" label={t('Mai fatto')} />;
  }
  const e = ESITO[riga.lastStatus] ?? { status: riga.lastStatus, label: riga.lastStatus };
  return (
    <div className="md-due-righe">
      <span><StatusChip status={e.status} label={t(e.label)} /></span>
      <span>
        {quandoRelativo(riga.lastAt)} · {peso(riga.lastSizeBytes)}
        {riga.verifiedAt && ` · ${t('verificato {quando}', { quando: quandoRelativo(riga.verifiedAt) })}`}
      </span>
    </div>
  );
}

/**
 * L'ultima prova di ripristino. «Riletto» dice che i byte si aprono;
 * «ricaricato» dice che dentro c'è un'azienda. Un'istanza mai provata lo
 * dice, perché è una risposta anche quella.
 */
function Prova({ riga }: { riga: BackupCoverage }) {
  if (!riga.restoreTestedAt) {
    return <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>{t('Mai provato')}</span>;
  }
  return (
    <div className="md-due-righe">
      <span>
        <StatusChip
          status={riga.restoreTestOk ? 'ready' : 'error'}
          label={riga.restoreTestOk ? t('Si ricarica') : t('Non si ricarica')}
        />
      </span>
      <span className="md-clamp">{quandoRelativo(riga.restoreTestedAt)}{riga.restoreTestError ? ` · ${riga.restoreTestError}` : ''}</span>
    </div>
  );
}

/** Come si ripristina: accanto all'originale, o al suo posto. */
type Modo = 'copia' | 'produzione';

/**
 * Tutto quello che si fa ai backup di un'istanza, in un pannello solo:
 * pianificazione, backup adesso, elenco, ripristino.
 *
 * Il ripristino non apre un secondo dialogo: cambia il corpo di questo. Un
 * dialogo dentro un dialogo nasconde ciò da cui si è partiti, e qui ciò da cui
 * si parte — *quale* snapshot — è la cosa che va tenuta sotto gli occhi.
 */
function PannelloIstanza({
  slug, riga, onClose,
}: { slug: string; riga: BackupCoverage; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const avvisa = useSnackbar();
  const [daRipristinare, setDaRipristinare] = useState<Backup | null>(null);
  const [modo, setModo] = useState<Modo>('copia');
  const [nome, setNome] = useState('');
  const [conferma, setConferma] = useState('');
  // Dove nasce la copia: il server dell'originale se è ancora pronto,
  // altrimenti il primo pronto. È la scelta che rende vero il ripristino
  // quando un server è perso.
  const servers = useServers(slug);
  const pronti = (servers.data?.servers ?? []).filter((s) => s.status === 'ready');
  const [serverId, setServerId] = useState('');
  const destinazione = serverId || (pronti.some((s) => s.id === riga.serverId) ? riga.serverId : pronti[0]?.id ?? '');

  const ripristina = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/backups/{id}/restore', {
          params: { path: { slug, id: daRipristinare!.id } },
          body: { name: nome, serverId: destinazione },
        }),
      ),
    onSuccess: (istanza) => {
      qc.invalidateQueries({ queryKey: ['instances', slug] });
      qc.invalidateQueries({ queryKey: ['backup-coverage', slug] });
      avvisa(t('Sto ripristinando in {nome}', { nome: istanza.name }));
      // Il ripristino è un job lungo quanto una creazione: si va dove si vede,
      // non si torna a una tavola che dirà «in creazione» per due minuti.
      if (istanza.jobId) {
        navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: istanza.jobId } });
      }
    },
  });

  // In produzione: lo snapshot prende il posto dei dati dell'istanza. Si
  // conferma scrivendo il nome, perché è l'unica operazione di questa pagina
  // che cambia qualcosa che esiste.
  const ripristinaQui = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/backups/{id}/restore-in-place', {
          params: { path: { slug, id: daRipristinare!.id } },
        }),
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['instances', slug] });
      qc.invalidateQueries({ queryKey: ['backup-coverage', slug] });
      avvisa(t('Sto riportando {nome} al backup', { nome: riga.instanceName }));
      navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
    },
  });

  const nomeOk = NOME_VALIDO.test(nome) && nome.length >= 2 && nome.length <= 63;
  const confermato = conferma.trim() === riga.instanceName;
  const scegli = (b: Backup, m: Modo) => {
    setModo(m);
    setConferma('');
    setDaRipristinare(b);
  };

  const titolo = !daRipristinare
    ? t('Backup di {nome}', { nome: riga.instanceName })
    : modo === 'copia'
      ? t('Ripristina {nome} come copia', { nome: riga.instanceName })
      : t('Riporta {nome} al backup', { nome: riga.instanceName });
  const descrizione = !daRipristinare
    ? riga.removed ? t('L’istanza non c’è più, i suoi backup sì: da uno di questi rinasce, sul server che scegli.') : undefined
    : modo === 'copia'
      ? t('Il backup diventa un’istanza nuova, sul server che scegli. L’originale non viene toccata.')
      : t('I dati attuali vengono sostituiti da quelli del backup, nella stessa istanza. Prima ne viene fatta una copia di sicurezza.');

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={titolo}
      description={descrizione}
      actions={
        daRipristinare ? (
          <>
            <Button variant="text" onClick={() => setDaRipristinare(null)}>{t('Indietro')}</Button>
            {modo === 'copia' ? (
              <Button
                variant="filled"
                icon={ripristina.isPending ? <Anello /> : undefined}
                disabled={!nomeOk || !destinazione || ripristina.isPending}
                onClick={() => ripristina.mutate()}
              >
                {ripristina.isPending ? t('Avvio…') : t('Ripristina')}
              </Button>
            ) : (
              <Button
                variant="filled"
                icon={ripristinaQui.isPending ? <Anello /> : undefined}
                disabled={!confermato || ripristinaQui.isPending}
                onClick={() => ripristinaQui.mutate()}
              >
                {ripristinaQui.isPending ? t('Avvio…') : t('Sostituisci i dati')}
              </Button>
            )}
          </>
        ) : (
          <Button variant="text" onClick={onClose}>{t('Chiudi')}</Button>
        )
      }
    >
      {daRipristinare && modo === 'copia' && (
        <FormRipristino
          backup={daRipristinare}
          origine={riga.instanceName}
          rimossa={!!riga.removed}
          nome={nome}
          onNome={setNome}
          nomeOk={nomeOk}
          server={pronti.map((s) => ({ value: s.id, label: s.id === riga.serverId ? t('{nome} — quello dell’originale', { nome: s.name }) : s.name }))}
          serverId={destinazione}
          onServer={setServerId}
          errore={ripristina.error as Error | null}
        />
      )}
      {daRipristinare && modo === 'produzione' && (
        <FormProduzione
          backup={daRipristinare}
          istanza={riga.instanceName}
          conferma={conferma}
          onConferma={setConferma}
          errore={ripristinaQui.error as Error | null}
        />
      )}
      {!daRipristinare && <Elenco slug={slug} riga={riga} onRipristina={scegli} />}
    </Dialog>
  );
}

function Elenco({
  slug, riga, onRipristina,
}: {
  slug: string;
  riga: BackupCoverage;
  onRipristina: (b: Backup, modo: Modo) => void;
}) {
  const permessi = usePermessiSu(slug, { istanza: riga.instanceId });
  const qc = useQueryClient();
  const navigate = useNavigate();
  const avvisa = useSnackbar();
  const id = riga.instanceId;

  const q = useQuery({
    queryKey: ['backups', slug, id],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/instances/{id}/backups', { params: { path: { slug, id } } })),
    refetchInterval: (r) =>
      (r.state.data?.backups ?? []).some((b) => b.status === 'running') ? 5000 : false,
  });
  const backups = q.data?.backups ?? [];

  const adesso = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/instances/{id}/backups', { params: { path: { slug, id } } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backups', slug, id] });
      qc.invalidateQueries({ queryKey: ['backup-coverage', slug] });
      avvisa(t('Backup avviato: lo vedi comparire qui sotto'));
    },
  });

  // La prova: l'ultimo backup riuscito si ricarica in un database a parte sul
  // server e si butta. L'hub la fa da sé ogni mese; da qui si fa adesso.
  const prova = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/instances/{id}/restore-test', { params: { path: { slug, id } } })),
    onSuccess: (r) => {
      avvisa(t('Provo a ricaricare l’ultimo backup di {nome}', { nome: riga.instanceName }));
      navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
    },
  });

  const scarica = (b: Backup) => {
    // Un file da scaricare, non una pagina: il browser lo salva e resta qui.
    window.location.assign(`/api/v1/orgs/${slug}/backups/${b.id}/download`);
  };

  // Di un'istanza rimossa non si pianifica, non si fa un backup e non si
  // prova niente: non c'è più una macchina su cui farlo. Restano i backup
  // fatti, da scaricare o da far rinascere altrove.
  const viva = !riga.removed;

  return (
    <div className="md-stack">
      {viva && <Pianificazione slug={slug} riga={riga} />}
      {viva && <Divider />}

      <div className="md-section-head">
        <h3 className="md-title-small">{t('Backup fatti')}</h3>
        {viva && (
          <div className="md-stack__inline">
            <ConPermesso ha={permessi} serve="backup.create">
              <Button variant="text" icon={prova.isPending ? <Anello /> : <Shield />} disabled={prova.isPending || backups.length === 0} onClick={() => prova.mutate()}>
                {prova.isPending ? t('Avvio…') : t('Prova il ripristino')}
              </Button>
            </ConPermesso>
            <ConPermesso ha={permessi} serve="backup.create">
              <Button variant="tonal" icon={adesso.isPending ? <Anello /> : <Play />} disabled={adesso.isPending} onClick={() => adesso.mutate()}>
                {adesso.isPending ? t('Avvio…') : t('Fai un backup adesso')}
              </Button>
            </ConPermesso>
          </div>
        )}
      </div>

      {adesso.error && (
        <Banner tone="error" title={t('Il backup non è partito')}>{(adesso.error as Error).message}</Banner>
      )}
      {prova.error && (
        <Banner tone="error" title={t('La prova non è partita')}>{(prova.error as Error).message}</Banner>
      )}
      {q.isPending && <Skeleton height="6rem" />}
      {q.error && (
        <Banner tone="error" title={t('Non riesco a leggere i backup')}>{q.error.message}</Banner>
      )}

      {q.isSuccess && backups.length === 0 && (
        <Banner tone="info" title={t('Nessun backup, ancora')}>
          {t('Di questa istanza non esiste nessuna copia. Fanne uno adesso, oppure attiva quelli automatici qui sopra.')}
        </Banner>
      )}

      {backups.length > 0 && (
        <DataTable
          columns={[
            { key: 'quando', header: t('Quando'), nowrap: true },
            { key: 'esito', header: t('Esito') },
            { key: 'peso', header: t('Dimensione'), numeric: true },
            { key: 'azioni', header: '' },
          ]}
          rowKey={(r) => String(r.id)}
          rows={backups.map((b) => {
            const e = ESITO[b.status] ?? { status: b.status, label: b.status };
            // Si ripristina solo ciò che ha uno snapshot e che non ha fallito la
            // rilettura: offrire il pulsante e poi rifiutare sarebbe una lista
            // che mente.
            const ripristinabile =
              !!b.snapshotId && b.status !== 'failed' && b.status !== 'verify_failed';
            return {
              id: b.id,
              quando: (
                <div className="md-due-righe">
                  <span>{quando(b.startedAt)}</span>
                  <span>{b.kind === 'manual' ? t('A mano') : t('Automatico')}</span>
                </div>
              ),
              esito: (
                <div className="md-due-righe">
                  <span className="md-stack__inline" style={{ alignItems: 'center' }}>
                    {/* Livello: il backup ha un fine ma non una percentuale. */}
                    {b.status === 'running' && <Logo size={22} anim="livello" label={t('Backup in corso')} />}
                    <StatusChip status={e.status} label={t(e.label)} />
                  </span>
                  {b.error && <span>{b.error}</span>}
                  {!b.error && b.restoreTestedAt && (
                    <span>{b.restoreTestOk ? t('ricaricato con successo {quando}', { quando: quandoRelativo(b.restoreTestedAt) }) : t('non si ricarica: {errore}', { errore: b.restoreTestError ?? '' })}</span>
                  )}
                </div>
              ),
              peso: peso(b.sizeBytes),
              // In colonna: il dialogo è stretto, e tre bottoni in fila ne
              // mostravano uno e mezzo.
              azioni: ripristinabile ? (
                <div className="md-stack" style={{ gap: 0, alignItems: 'flex-end' }}>
                  <ConPermesso ha={permessi} serve="backup.download">
                    <Button variant="text" icon={<Download />} onClick={() => scarica(b)}>{t('Scarica')}</Button>
                  </ConPermesso>
                  <ConPermesso ha={permessi} serve="db.restore">
                    <Button variant="text" icon={<Restore />} onClick={() => onRipristina(b, 'copia')}>{viva ? t('Come copia') : t('Ripristina')}</Button>
                  </ConPermesso>
                  {viva && (
                    <ConPermesso ha={permessi} serve={['db.restore', 'db.drop']}>
                      <Button variant="text" onClick={() => onRipristina(b, 'produzione')}>{t('In produzione')}</Button>
                    </ConPermesso>
                  )}
                </div>
              ) : null,
            };
          })}
        />
      )}
    </div>
  );
}

/**
 * La pianificazione dell'istanza. Le quattro regole di conservazione arrivano
 * dalla copertura e si ripropongono: reimpostare il cron senza saperle
 * riporterebbe di nascosto ai valori predefiniti una conservazione scelta.
 */
function Pianificazione({ slug, riga }: { slug: string; riga: BackupCoverage }) {
  const permessi = usePermessiSu(slug, { istanza: riga.instanceId });
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const id = riga.instanceId;
  const iniziale = riga.cron ?? '@daily';
  const notoIniziale = PIANI.some((p) => p.value === iniziale);
  const [scelta, setScelta] = useState(notoIniziale ? iniziale : 'custom');
  const [cron, setCron] = useState(iniziale);
  const [keep, setKeep] = useState({
    keepHourly: riga.keepHourly ?? 0,
    keepDaily: riga.keepDaily ?? 0,
    keepWeekly: riga.keepWeekly ?? 0,
    keepMonthly: riga.keepMonthly ?? 0,
  });

  const cronEffettivo = scelta === 'custom' ? cron : scelta;

  const invalida = () => {
    qc.invalidateQueries({ queryKey: ['backup-coverage', slug] });
  };

  const salva = useMutation({
    mutationFn: () =>
      must(
        api.PUT('/api/v1/orgs/{slug}/instances/{id}/backup-schedule', {
          params: { path: { slug, id } },
          body: { cron: cronEffettivo, ...keep },
        }),
      ),
    onSuccess: () => {
      invalida();
      avvisa(t('Backup automatici di {nome}: {piano}', { nome: riga.instanceName, piano: nomePiano(cronEffettivo).toLowerCase() }));
    },
  });

  const togli = useMutation({
    mutationFn: () =>
      mustOk(
        api.DELETE('/api/v1/orgs/{slug}/instances/{id}/backup-schedule', {
          params: { path: { slug, id } },
        }),
      ),
    onSuccess: () => {
      invalida();
      avvisa(t('Backup automatici di {nome} disattivati', { nome: riga.instanceName }));
    },
  });

  const campo = (chiave: keyof typeof keep, label: string, predefinito: number) => (
    <TextField
      label={label}
      type="number"
      min={0}
      value={String(keep[chiave])}
      placeholder={String(predefinito)}
      onChange={(e) => setKeep({ ...keep, [chiave]: Number(e.target.value) })}
    />
  );

  return (
    <div className="md-stack">
      <div className="md-section-head">
        <h3 className="md-title-small">{t('Backup automatici')}</h3>
        {riga.cron
          ? <StatusChip status="ready" label={t('Attivi')} />
          : <StatusChip status="error" label={t('Non attivi')} />}
      </div>

      {salva.error && (
        <Banner tone="error" title={t('Non sono riuscito a salvare')}>{(salva.error as Error).message}</Banner>
      )}
      {togli.error && (
        <Banner tone="error" title={t('Non sono riuscito a disattivarli')}>{(togli.error as Error).message}</Banner>
      )}

      <Select
        label={t('Ogni quanto')}
        value={scelta}
        options={[...PIANI.map((p) => ({ ...p, label: t(p.label) })), { value: 'custom', label: t('Personalizzata…') }]}
        onChange={(e) => setScelta(e.target.value)}
      />
      {scelta === 'custom' && (
        <TextField
          label={t('Regola')}
          value={cron}
          onChange={(e) => setCron(e.target.value)}
          help={t('Nel formato cron, cinque colonne: es. «0 3 * * *» per le tre di notte.')}
        />
      )}

      <Details summary={t('Quante copie tenere')}>
        <div className="md-stack">
          <div className="md-form__numeri">
            {campo('keepHourly', t('Orarie'), 24)}
            {campo('keepDaily', t('Giornaliere'), 7)}
            {campo('keepWeekly', t('Settimanali'), 4)}
            {campo('keepMonthly', t('Mensili'), 12)}
          </div>
          <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Quante copie conservare per ciascun periodo; le più vecchie vengono eliminate da Molo. Lasciando zero valgono i valori consigliati: 24 orarie, 7 giornaliere, 4 settimanali, 12 mensili.')}
          </p>
        </div>
      </Details>

      <div className="md-stack__inline">
        <ConPermesso ha={permessi} serve="backup.schedule">
          <Button variant="filled" icon={salva.isPending ? <Anello /> : undefined} disabled={salva.isPending || cronEffettivo.trim() === ''} onClick={() => salva.mutate()}>
            {salva.isPending ? t('Salvo…') : riga.cron ? t('Salva') : t('Attiva|azione')}
          </Button>
        </ConPermesso>
        {riga.cron && (
          <ConPermesso ha={permessi} serve="backup.schedule">
            <Button variant="text" disabled={togli.isPending} onClick={() => togli.mutate()}>
              {t('Disattiva')}
            </Button>
          </ConPermesso>
        )}
      </div>
    </div>
  );
}

const NOME_VALIDO = /^[a-z0-9][a-z0-9-]*$/;

/**
 * Il ripristino chiede una cosa sola: come si chiamerà la copia. Non c'è un
 * valore predefinito perché il nome finisce nell'unit, nel vhost e nel
 * database, ed è il momento in cui chi ripristina decide se sta facendo una
 * prova o il rimpiazzo della produzione.
 */
function FormRipristino({
  backup, origine, rimossa, nome, onNome, nomeOk, server, serverId, onServer, errore,
}: {
  backup: Backup;
  origine: string;
  rimossa: boolean;
  nome: string;
  onNome: (v: string) => void;
  nomeOk: boolean;
  server: Array<{ value: string; label: string }>;
  serverId: string;
  onServer: (v: string) => void;
  errore: Error | null;
}) {
  return (
    <div className="md-stack">
      {errore && (
        <Banner tone="error" title={t('Il ripristino non è partito')}>{errore.message}</Banner>
      )}

      <Banner tone="info" title={t('Dal backup del {data}', { data: quando(backup.startedAt) })}>
        {rimossa
          ? t("Nasce un'istanza nuova con i dati di {origine}.", { origine: <b>{origine}</b> })
          : t("Nasce un'istanza nuova accanto a {origine}, che resta accesa e intatta.", { origine: <b>{origine}</b> })}{' '}
        {t('La copia parte {sicura}: niente email in uscita, niente operazioni automatiche — così una copia di produzione non manda fatture ai clienti veri.', {
          sicura: <b>{t('in modalità sicura')}</b>,
        })}
      </Banner>

      {server.length === 0 ? (
        <Banner tone="error" title={t('Nessun server pronto')}>
          {t("La copia ha bisogno di un server che abbia finito l'installazione. Collegane uno e aspetta che diventi «Pronto».")}
        </Banner>
      ) : (
        <Select
          label="Server"
          value={serverId}
          options={server}
          onChange={(e) => onServer(e.target.value)}
          help={t('Dove nasce la copia. Il backup sta nella destinazione dell’azienda, non sul server: può rinascere su qualunque macchina pronta, anche se quella originale non c’è più.')}
        />
      )}

      <TextField
        label={t('Nome della copia')}
        value={nome}
        autoFocus
        onChange={(e) => onNome(e.target.value.toLowerCase())}
        error={nome.length > 0 && !nomeOk}
        help={
          nome.length > 0 && !nomeOk
            ? t('Solo minuscole, cifre e trattini, a partire da una lettera o una cifra.')
            : t('Es. «{nome}-test». Non si cambia più.', { nome: origine })
        }
      />
    </div>
  );
}

/**
 * Il ripristino in produzione chiede il nome dell'istanza, scritto: è
 * l'unica operazione della pagina che sostituisce dati che esistono, e un
 * bottone rosso non basta a fermare una mano che va di fretta.
 */
function FormProduzione({
  backup, istanza, conferma, onConferma, errore,
}: {
  backup: Backup;
  istanza: string;
  conferma: string;
  onConferma: (v: string) => void;
  errore: Error | null;
}) {
  return (
    <div className="md-stack">
      {errore && (
        <Banner tone="error" title={t('Il ripristino non è partito')}>{errore.message}</Banner>
      )}

      <Banner tone="error" title={t('I dati di {istanza} tornano al {data}', { istanza, data: quando(backup.startedAt) })}>
        {t('Tutto ciò che è stato fatto dopo quel momento sparisce da {istanza}: ordini, fatture, allegati. Prima di toccare qualcosa viene fatto un backup di sicurezza dei dati attuali, e Odoo resta acceso finché i dati nuovi non sono pronti. Le sessioni aperte vengono chiuse.', {
          istanza: <b>{istanza}</b>,
        })}
      </Banner>

      <TextField
        label={t('Scrivi il nome dell’istanza per confermare')}
        value={conferma}
        autoFocus
        onChange={(e) => onConferma(e.target.value)}
        help={t('Scrivi «{nome}».', { nome: istanza })}
      />
    </div>
  );
}
